// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------
function getScrollableAncestors(el) {
    const ancestors = [];
    let current = el.parentElement;
    while (current) {
        const { overflowY, overflowX } = getComputedStyle(current);
        if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflowX)) {
            ancestors.push(current);
        }
        current = current.parentElement;
    }
    ancestors.push(window);
    return ancestors;
}
function throttle(fn, wait) {
    let timer = null;
    let lastTime = 0;
    return function () {
        const now = Date.now();
        const remaining = wait - (now - lastTime);
        if (remaining <= 0) {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            lastTime = now;
            fn();
        }
        else {
            if (timer !== null)
                clearTimeout(timer);
            timer = setTimeout(() => {
                lastTime = Date.now();
                timer = null;
                fn();
            }, remaining);
        }
    };
}
/**
 * Misst die Position und ruft den Handler NUR bei tatsächlicher Änderung auf.
 * Schreibt direkt in state.lastSnapshot (by-reference) – kein Closure nötig.
 */
function measure(el, handlerRef, state) {
    const rect = el.getBoundingClientRect();
    let scrollParent = el.parentElement;
    while (scrollParent) {
        const { overflowY, overflowX } = getComputedStyle(scrollParent);
        if (/(auto|scroll|overlay)/.test(overflowY + overflowX))
            break;
        scrollParent = scrollParent.parentElement;
    }
    const scrollTop = scrollParent ? scrollParent.scrollTop : window.scrollY;
    const scrollLeft = scrollParent ? scrollParent.scrollLeft : window.scrollX;
    const next = {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
        x: rect.left,
        y: rect.top,
        scrollTop,
        scrollLeft,
    };
    // Dirty-Check: kein Handler-Aufruf wenn sich nichts geändert hat
    const prev = state.lastSnapshot;
    if (prev !== null &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.bottom === next.bottom &&
        prev.right === next.right &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.scrollTop === next.scrollTop &&
        prev.scrollLeft === next.scrollLeft) {
        return;
    }
    state.lastSnapshot = next;
    handlerRef.current({ ...next, el });
}
function normalizeOptions(value) {
    return typeof value === "function"
        ? { handler: value, throttle: 50 }
        : { throttle: 50, ...value };
}
// ---------------------------------------------------------------------------
// Directive
// ---------------------------------------------------------------------------
export const vTrackPosition = {
    mounted(el, binding) {
        const { handler, throttle: throttleMs = 50 } = normalizeOptions(binding.value);
        if (typeof handler !== "function") {
            console.warn("[v-track-position] Kein gültiger Handler übergeben.");
            return;
        }
        // handlerRef als Box: measure() zeigt immer auf den aktuellen Handler,
        // ohne dass Listener neu registriert werden müssen.
        const handlerRef = { current: handler };
        const state = {
            handlerRef,
            throttledMeasure: () => { },
            resizeObserver: null,
            ancestors: [],
            lastSnapshot: null,
        };
        const throttledMeasure = throttle(() => measure(el, handlerRef, state), throttleMs);
        state.throttledMeasure = throttledMeasure;
        // Initiale Messung (nach einem Tick, damit das Layout stabil ist)
        requestAnimationFrame(throttledMeasure);
        // ResizeObserver: Größenänderungen des Elements selbst
        const resizeObserver = new ResizeObserver(throttledMeasure);
        resizeObserver.observe(el);
        state.resizeObserver = resizeObserver;
        // Scroll-Events aller scrollbaren Vorfahren (inkl. window)
        const ancestors = getScrollableAncestors(el);
        ancestors.forEach((a) => a.addEventListener("scroll", throttledMeasure, { passive: true }));
        state.ancestors = ancestors;
        // Globales resize-Event
        window.addEventListener("resize", throttledMeasure, { passive: true });
        el.__trackPosition__ = state;
    },
    updated(el, binding) {
        // WICHTIG: Kein measure() aufrufen!
        // updated feuert bei jedem Re-Render der Eltern-Komponente – auch bei
        // dem Re-Render, den der Handler selbst ausgelöst hat. Würden wir hier
        // measure() aufrufen, entsteht der Loop trotz Dirty-Check.
        //
        // Lösung: Nur den Handler-Zeiger in der handlerRef-Box aktualisieren.
        // Die bestehende throttledMeasure-Closure liest handlerRef.current und
        // verwendet automatisch den neuen Handler beim nächsten echten Event.
        if (!el.__trackPosition__)
            return;
        const { handler } = normalizeOptions(binding.value);
        if (typeof handler !== "function")
            return;
        el.__trackPosition__.handlerRef.current = handler;
    },
    unmounted(el) {
        if (!el.__trackPosition__)
            return;
        const { throttledMeasure, resizeObserver, ancestors } = el.__trackPosition__;
        resizeObserver.disconnect();
        ancestors.forEach((a) => a.removeEventListener("scroll", throttledMeasure));
        window.removeEventListener("resize", throttledMeasure);
        delete el.__trackPosition__;
    },
};
export default vTrackPosition;
// ---------------------------------------------------------------------------
// Plugin-Export für app.use()
// ---------------------------------------------------------------------------
export const TrackPositionPlugin = {
    install(app) {
        app.directive("resize2", vTrackPosition);
    },
};
